import { OCEAN } from "../../../shared/Config";
import { normaliseAngle, shortestAngle } from "../../../shared/Sailing";

/**
 * The waves: what the sea looks like, and nothing else.
 *
 * This used to be `shared/Ocean.ts` — a field the arena published, both ends evaluated, and hulls
 * were pushed by. It is a **picture** now (design/Water.md), so it lives on the
 * client, the server has never heard of it, and two players may be looking at slightly different
 * water. That is the same bargain the wake and the school have always made.
 *
 * What it still owes anybody is a **height and a slope at a point**, because hulls rise and roll on
 * it and coins swell with it. That is the one constraint on how the sea may be drawn: the large
 * waves have to be a formula the CPU can also evaluate, not something that only exists on the GPU.
 *
 * ## What the sea is made of
 *
 * A **spectrum of Stokes trains**, not a sine wave (design/Water.md). Four
 * trains at falling wavelengths, spread either side of the wind, each with a crest sharper than a
 * sine's and a trough flatter — which is the difference between water and a bedsheet. Every one of
 * them is still `amplitude · f(k · r − phase)`, so every one of them is still a formula both the
 * CPU and the shader can evaluate at a point.
 *
 * ## Everything is measured from the camera
 *
 * Not from the world, and not from a frame that accumulates as the camera travels. A wave is
 * `sin(k · (r · d) − phase)` where **`r` is the offset from the camera**, and the distance the
 * camera has covered along the wave is folded into the phase instead:
 *
 * ```
 * phase += rate * dt − k * (cameraMoved · d)
 * ```
 *
 * That one line replaces a good deal. The phase wraps at 2 PI so nothing grows without bound and
 * float precision never decays, however long a battle runs. There is no world origin, so the sea's
 * direction can turn continuously without the pattern tearing somewhere far away — which is what
 * would happen if the waves were pinned to a distant point and then rotated about it. And the
 * arena's seam costs nothing, because a camera either side of it is still the camera: the torus
 * lattice that used to be needed for that is gone with the rest of the shared machinery.
 */

/** How far apart the crests are, in world pixels. **Tunable.** */
export let WAVE_LENGTH = OCEAN.waveLength;

/** How fast a crest travels, in world pixels per second. **Tunable.** */
export let WAVE_SPEED = OCEAN.waveSpeed;

/** How tall the sea reads, 0 for a flat calm and 1 for as much as the renderer will give. **Tunable.** */
export let WAVE_HEIGHT = OCEAN.waveHeight;

/**
 * How long the sea takes to swing round to a new wind, in seconds — the time constant of an ease
 * rather than a delay before a jump. **Tunable.**
 *
 * It was `swellLagSeconds`, the lag that held the swell's direction away from the wind's so that a
 * captain had to choose between them. Nothing chooses anything now, so what is left of it is a
 * look: a sea that snapped round the instant the weather did would read as weather rather than as
 * water, and one that never caught up would read as neither.
 */
export let WAVE_TURN_SECONDS = OCEAN.waveTurnSeconds;

/**
 * How sharp a crest is, against a plain sine's.
 *
 * This is the **second-order Stokes** term: `sin θ + q·(2 sin²θ − 1)`, which lifts the crest into a
 * ridge and flattens the trough into a broad shallow. It is what real deep water does, and it is
 * most of the difference between a sea and a corrugated sheet.
 *
 * It is **not** a Gerstner wave, which is what the sea was first specified as, and the reason is
 * the constraint at the top of this file. A Gerstner wave moves each point in a
 * circle, so the surface is *parametric*: to answer "how high is the water at this point" you have
 * to invert the horizontal displacement, which is an iteration rather than a formula. A hull
 * needs that answer sixty times a second and so does every coin. The Stokes profile buys the same
 * silhouette — sharp above, flat below — as a plain function of position, evaluable on both sides
 * and cheaper than a Gerstner sum besides.
 *
 * Above about 0.5 the crest grows a dimple and the water starts to read as a cartoon of itself.
 */
const CREST_SHARPNESS = 0.35;

/** Puts the sharpened crest back at 1, so amplitudes still mean what they say. */
const CREST_NORM = 1 / (1 + CREST_SHARPNESS);

/**
 * The sea, as a list of trains relative to the wind: how long each is against `waveLength`, how
 * much of the sea's height it carries, and how far off the wind it runs.
 *
 * Four things are going on in this table, and all four matter more than the exact numbers:
 *
 * - **The scales are not halves.** 0.52, 0.27, 0.13 rather than 0.5, 0.25, 0.125 — because
 *   commensurate wavelengths share harmonics, and a sea built out of them repeats on a period you
 *   can see. Detuned, the pattern never quite comes back round.
 * - **Weight tracks scale**, so every train is about as *steep* as every other. That is what a wind
 *   sea actually looks like: the small waves are not gentler, they are smaller. Weight falling
 *   faster than scale gives a long smooth swell with a dusting on top, which reads as calm.
 * - **The weights sum to one**, so a crest with every train in phase reaches exactly 1 and the
 *   whitecap thresholds keep meaning what they meant when the sea was a single wave.
 * - **The spread grows with the shortening**, and alternates side. A real sea is directional but
 *   not a line; the short chop is the part that wanders furthest off the wind.
 */
const WAVE_SPECTRUM = [
    { scale: 1, weight: 0.56, bearing: 0 },
    { scale: 0.52, weight: 0.24, bearing: 0.26 },
    { scale: 0.27, weight: 0.13, bearing: -0.42 },
    { scale: 0.13, weight: 0.07, bearing: 0.66 }
];

/** How many trains the sea is built from. The shader unrolls its sum over exactly this many. */
export const WAVE_TRAINS = WAVE_SPECTRUM.length;

/**
 * What a gradient has to be multiplied by to land around one on the steepest water there is.
 *
 * A raw slope is height per world pixel — a hundredth or so, an awkward number to write a tuning
 * constant against and one that would change meaning every time somebody dragged `waveLength` or
 * edited the spectrum. Everything that reads a slope for a *look* scales by this first, and then
 * its own constant reads as a plain fraction of "as steep as this sea gets".
 *
 * Computed from the spectrum rather than typed, because it is a **consequence** of it: add a train
 * and the sea gets steeper, and every constant downstream would silently mean something else. The
 * profile's own steepest point is found by scanning it, which costs a thousand `sin` once.
 */
export let SLOPE_UNIT = 1 / steepestSlope();

/** The four numbers above, as one thing to hand over. See `setWaveDials`. */
export interface WaveDials {
    length: number;
    speed: number;
    height: number;
    turnSeconds: number;
}

/**
 * Puts new numbers behind the four dials above, and recomputes what falls out of them.
 *
 * **For the dashboard's workspace and nothing else** (design/Settings.md). The game itself reads
 * `config/ocean.json` once at module load and never moves — every boat in a room has to agree
 * about the water it is riding, and a value hot-swapped mid-battle is exactly the drift
 * design/Networking.md is built to avoid. The editor is a single client looking at its own sea
 * with no arena behind it, so there is nothing there to disagree with, and a swell you have to
 * restart to see is a swell nobody tunes.
 *
 * The four exports are `let` rather than `const` for this, and are read as live bindings by
 * everything that imports them — so a change here reaches `Water`, `Boat.ride` and the shader's
 * uniforms with nothing to plumb. Two things do **not** follow on their own: a `Waves` already
 * laid out keeps the wavelengths it was built with until `refit`, and `OceanFilter` holds the
 * height in a uniform until `Water.setSwell`.
 */
export function setWaveDials(dials: Partial<WaveDials>): void {
    if (dials.length !== undefined) WAVE_LENGTH = Math.max(1, dials.length);
    if (dials.speed !== undefined) WAVE_SPEED = dials.speed;
    if (dials.height !== undefined) WAVE_HEIGHT = dials.height;
    if (dials.turnSeconds !== undefined) WAVE_TURN_SECONDS = dials.turnSeconds;

    // A consequence of the wavelength, so it cannot be left behind by one.
    SLOPE_UNIT = 1 / steepestSlope();
}

/** One travelling train. Plain data, and never anything but this client's own. */
export interface WaveTrain {
    /** Radians, 0 = north, the direction the crests travel toward. */
    heading: number;
    /** Radians this train runs off the wind. What keeps a spectrum a spectrum as the wind turns. */
    bearing: number;
    /** World pixels between crests. */
    wavelength: number;
    /** How much of the sea this train is, 0 to 1. */
    amplitude: number;
    /** Radians, wrapping at 2 PI — the phase **at the camera**. See the note at the top. */
    phase: number;
}

/**
 * The wave profile and its slope against the phase: `(f(θ), df/dθ)`.
 *
 * **This is the function that also exists in GLSL**, forty lines below. Change one and change the
 * other in the same edit — see the note on WAVE_GLSL.
 */
function crest(theta: number): { height: number; slope: number } {
    const sine = Math.sin(theta);

    return {
        height: (sine + CREST_SHARPNESS * (2 * sine * sine - 1)) * CREST_NORM,
        slope: Math.cos(theta) * (1 + 4 * CREST_SHARPNESS * sine) * CREST_NORM
    };
}

/**
 * The steepest the whole spectrum can get, in height per world pixel: every train at its own
 * steepest, all pulling the same way. Scanned rather than solved — the profile's maximum has no
 * tidy closed form once the Stokes term is in it, and this runs once at module load.
 */
function steepestSlope(): number {
    let peak = 0;
    for (let i = 0; i < 1000; i++) peak = Math.max(peak, Math.abs(crest((i / 1000) * Math.PI * 2).slope));

    return WAVE_SPECTRUM.reduce((sum, band) => sum + (band.weight * peak * (Math.PI * 2)) / (WAVE_LENGTH * band.scale), 0);
}

/**
 * How fast a train's phase turns, in radians per second.
 *
 * **Long waves outrun short ones**, which is the one piece of real physics in here and is worth the
 * line it costs: deep-water phase speed goes as the square root of the wavelength. A sea whose
 * every train travelled at `waveSpeed` has the fine chop racing across the swell it is sitting on,
 * which reads as rain on a window. `waveSpeed` is therefore the speed of the **longest** train, and
 * everything shorter is slower.
 */
function phaseRate(wavelength: number): number {
    return (Math.PI * 2 * WAVE_SPEED) / Math.sqrt(wavelength * WAVE_LENGTH);
}

/** Anything that reads the sea only walks it, so that is all it asks for. */
export type Sea = Iterable<WaveTrain>;

/** Folds a phase back into [0, 2 PI). Correct for negatives, which the camera term produces. */
export function wrapPhase(phase: number): number {
    const turn = Math.PI * 2;
    return ((phase % turn) + turn) % turn;
}

/**
 * How high the water is, in units of one full-strength train — so [-1, 1] for a sea whose
 * amplitudes sum to one.
 *
 * `x` and `y` are **offsets from the camera**, not world coordinates. Everything that reads the sea
 * has a camera to hand and already knows how to fold the arena's seam out with `wrapDelta`; asking
 * for the offset rather than the position is what keeps this module free of the world's geometry.
 */
export function waveHeightAt(sea: Sea, x: number, y: number): number {
    let height = 0;

    for (const train of sea) {
        const k = (Math.PI * 2) / train.wavelength;
        height += train.amplitude * crest(k * (x * Math.sin(train.heading) - y * Math.cos(train.heading)) - train.phase).height;
    }

    return height;
}

/**
 * How steeply the water rises along a heading, at an offset from the camera — the surface's slope
 * in that one direction, in height per world pixel.
 *
 * Passing a hull's heading gives the slope along its keel; passing the heading plus a quarter turn
 * gives the slope across its beam, which is what a boat heels to. Multiply by SLOPE_UNIT for a
 * number around one.
 */
export function waveSlopeAt(sea: Sea, x: number, y: number, heading: number): number {
    let slope = 0;

    for (const train of sea) {
        const k = (Math.PI * 2) / train.wavelength;
        const at = k * (x * Math.sin(train.heading) - y * Math.cos(train.heading)) - train.phase;
        slope += train.amplitude * k * crest(at).slope * Math.cos(train.heading - heading);
    }

    return slope;
}

/**
 * A train's wave vector, in radians per world pixel — `k * d`, the form the shader wants.
 *
 * The GPU has no use for a heading and a wavelength separately, and working them back into a vector
 * per pixel would be two trig calls per train per pixel for a number that changes once a frame.
 * `out` is filled and returned, so a per-frame uniform update allocates nothing.
 */
export function waveVector(train: WaveTrain, out: { x: number; y: number }): { x: number; y: number } {
    const k = (Math.PI * 2) / train.wavelength;
    out.x = k * Math.sin(train.heading);
    out.y = -k * Math.cos(train.heading);

    return out;
}

/**
 * The sea itself: the trains, their clock, and the slow swing round to the weather.
 *
 * One of these per scene. It is handed to [Water.ts](Water.ts) to be drawn and read by whatever
 * rides on it — see `Boat.ride` and [LootField.ts](LootField.ts) — so there is one sea on this
 * client rather than one per consumer.
 */
export class Waves {
    private readonly _trains: WaveTrain[] = [];
    /** Whether a wind has ever been handed to `update`. See the note there. */
    private _settled = false;

    /** The sea, for drawing and for riding. Live objects: read them, do not keep them. */
    public get trains(): readonly WaveTrain[] {
        return this._trains;
    }

    constructor() {
        this.spread(0);
    }

    /**
     * Lays the spectrum out around a wind. Each train keeps its own bearing off that wind, so the
     * sea stays a spectrum rather than collapsing into one direction as the weather turns.
     *
     * Phases start random and independent, which is the whole reason a sum of four trains reads as
     * water: seeded together they would all crest at the origin and the sea would have a middle.
     */
    public spread(wind: number): void {
        this._trains.length = 0;
        for (const band of WAVE_SPECTRUM) {
            this._trains.push({
                heading: normaliseAngle(wind + band.bearing),
                bearing: band.bearing,
                wavelength: WAVE_LENGTH * band.scale,
                amplitude: band.weight,
                phase: Math.random() * Math.PI * 2
            });
        }
    }

    /**
     * Re-measures the trains against the current `WAVE_LENGTH`, keeping their phases.
     *
     * The companion to `setWaveDials`, and the reason it is not `spread` again: a spread re-rolls
     * every phase, and a sea that reseeded itself on every frame of a dragged slider would boil
     * rather than stretch. Only the dashboard calls it; in a battle the wavelength never moves.
     */
    public refit(): void {
        for (let i = 0; i < this._trains.length; i++) this._trains[i].wavelength = WAVE_LENGTH * WAVE_SPECTRUM[i].scale;
    }

    /**
     * Moves the sea on a frame: the crests travel, the camera slides across them, and the whole
     * pattern eases round toward the wind.
     *
     * `movedX` and `movedY` are how far the camera went this frame, in world pixels, already
     * measured with `wrapDelta` by whoever owns it — so the seam is folded out before this ever
     * sees it. The camera's travel goes into the **phase** rather than into a position, which is
     * what keeps the sea bounded and lets its direction turn without tearing. See the note at the
     * top of this file.
     */
    public update(deltaSeconds: number, wind: number, movedX: number, movedY: number): void {
        // A time constant rather than a rate: the sea covers the same fraction of whatever is left
        // each second, so it hurries when the weather has run away from it and settles as it
        // catches up. Framerate-independent, like every other ease in the game.
        //
        // Except on the first frame, which arrives before anybody knows the wind: the trains are
        // built pointing north and would spend a whole WAVE_TURN_SECONDS swinging round off a
        // direction that was never the weather. So the first wind a sea hears, it takes whole.
        const caught = this._settled ? 1 - Math.exp(-deltaSeconds / Math.max(WAVE_TURN_SECONDS, 0.001)) : 1;
        this._settled = true;

        for (const train of this._trains) {
            // Toward the wind **plus its own bearing**, so the spread survives the turn.
            const wanted = normaliseAngle(wind + train.bearing);
            train.heading = normaliseAngle(train.heading + shortestAngle(train.heading, wanted) * caught);

            const k = (Math.PI * 2) / train.wavelength;
            const along = movedX * Math.sin(train.heading) - movedY * Math.cos(train.heading);
            train.phase = wrapPhase(train.phase + phaseRate(train.wavelength) * deltaSeconds - k * along);
        }
    }
}

/** What each train's uniform is called in the shader, one name per train. */
export const WAVE_UNIFORMS = Array.from({ length: WAVE_TRAINS }, (unused, i) => `uWave${i}`);

/**
 * **The same sum, in GLSL.** Paste it into a fragment shader and call `oceanWaves(offsetFromCamera)`;
 * it answers `vec3(height, dH/dx, dH/dy)`, the height being the number `waveHeightAt` returns.
 *
 * It lives here, a few lines from the TypeScript it has to agree with, and that placement is the
 * whole mitigation for the field existing twice. **If you change the maths above, change it here in
 * the same edit.** It has already earned that once: the phase's sign was wrong in both copies and
 * the fix was a single edit.
 *
 * A train is packed as `vec4(kx, ky, amplitude, phase)` — the wave vector rather than a heading and
 * a wavelength. One named uniform per train with the sum unrolled, never an array: `uniform vec4
 * u[2]` is the obvious way to write it and it rendered perfectly under a headless browser's
 * software GL while drawing nothing at all on real hardware.
 *
 * The profile's constants are **interpolated in** rather than declared as uniforms, so there is one
 * place `CREST_SHARPNESS` is written down and no way for the two halves to be handed different
 * numbers at runtime.
 *
 * Everything is `highp`, explicitly. Left to the fragment default PixiJS injects — `mediump` — the
 * arithmetic loses the small differences between neighbouring pixels and the sea flattens into a
 * gradient, on some drivers and not others.
 */
export const WAVE_GLSL = `
${WAVE_UNIFORMS.map((name) => `uniform highp vec4 ${name};`).join("\n")}

// The Stokes profile and its slope against the phase — the twin of crest() above.
highp vec2 oceanCrest(highp float theta)
{
    highp float s = sin(theta);

    return vec2(s + ${CREST_SHARPNESS.toFixed(4)} * (2.0 * s * s - 1.0), cos(theta) * (1.0 + ${(4 * CREST_SHARPNESS).toFixed(4)} * s)) * ${CREST_NORM.toFixed(6)};
}

highp vec3 oceanWaveTrain(highp vec4 train, highp vec2 p)
{
    highp vec2 k = train.xy;
    highp vec2 wave = oceanCrest(dot(k, p) - train.w);

    return vec3(train.z * wave.x, train.z * wave.y * k);
}

highp vec3 oceanWaves(highp vec2 p)
{
    return ${WAVE_UNIFORMS.map((name) => `oceanWaveTrain(${name}, p)`).join(" + ")};
}
`;
